/**
 * Single source of truth for client-side cost previews.
 * Mirrors the canonical formulas in node/queries.js — change both together.
 *
 * Currency rules (owner ruling 2026-09-03):
 * - PurchasePrice, ShippingCost, LandTranCost and ClearingCost are foreign-currency
 *   and convert at ExchangeRate.
 * - ClearingUG, OtherExpenses and commissions are UGX.
 * - Demurrage is VAT-inclusive.
 *
 * The 2026-09-03 ruling SUPERSEDES the one of 2026-07-05, which had LandTranCost
 * and ClearingCost as UGX. fields.js had labelled them "Inland Transport (USD)"
 * and "Clearing Cost MBA (USD)" all along, so the form asked for USD and the
 * arithmetic read the answer as UGX. Production was sampled before the change:
 * the entered values are USD throughout (120-2000, against a ClearingUG of
 * 100,000-300,000 on the very same rows). Unconverted they contributed about
 * 0.003% of landed cost, which is why nobody noticed the two lines were inert.
 * - Inventory costs use TargetCommission; sold/committed vehicles use the actual
 *   AgreedCommission (converted at the row's ExchangeRate when CommissionCurrency is USD).
 * - Demurrage runs to today while the vehicle is in the bond and FREEZES at the
 *   handover once it has been delivered, matching the server's two clocks. See
 *   demurrageDays.
 *
 * WHAT THESE ARE FOR, and what they are not. They exist so the figures move
 * while a cost is being typed — the server cannot answer between keystrokes. The
 * server sends all five of these columns too, and on a record nobody is editing
 * the two answers must be the same answer. Both record modals used to
 * `Object.assign` the result over their own state during render, which did not
 * reconcile the two so much as destroy the evidence that they differed: after it
 * there was only one number left to read, and it was this one.
 */

// Every operand is coerced before it reaches a `+`. This is not defensive
// styling — it is load-bearing. normalizeRecords turns a NULL decimal into ""
// (UtilityFunctions.jsx formatFromInput), and `0 + ""` is the STRING "0", so a
// single NULL cost column turned TotalCost into a string and made every later
// term concatenate instead of add: entering a daily demurrage of 50,000 against
// a 24,060,000 vehicle displayed 2,406,000,044,840,000.
//
// Both vehicle modals used to coerce their own copy of the row on open, which is
// why this went unnoticed — but three paths reach here without that: add mode, a
// restored add-mode draft, and the replace_record socket listener. A formula
// module that is the "single source of truth" cannot depend on each caller
// having cleaned its input first.
const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Midnight LOCAL on the calendar day a value names, or null if it names none.
 *
 * `new Date("2026-05-02")` is UTC midnight by specification, and the day count
 * below used to measure from there to a LOCAL `Date()`. In EAT (UTC+3) the clock
 * therefore started three hours late, so between local midnight and 03:00 the
 * modal read one day SHORT of the sheet — every day, in a window nobody happens
 * to take screenshots in. It is the same parse that sent every date range a day
 * early before toLocalYMD; see UtilityFunctions' note on formatLongDate.
 *
 * Returning null rather than an Invalid Date is also what keeps the epoch trap
 * below closed: there is no input here that quietly becomes a real date.
 */
const startOfLocalDay = (value) => {
    //TWO SHAPES, AND AN OUTRIGHT REFUSAL FOR EVERYTHING ELSE. Those two are the
    //only ones that reach here: formatFromInput's "date" branch splits a string
    //at the T and passes a non-string through untouched, so a date column is
    //either `YYYY-MM-DD…` or a Date. Anything else is not a date, and the point
    //of the whole guard is that it must not be guessed at.
    //
    //A permissive `new Date(s)` is what made this dangerous. `new Date(null)` is
    //the EPOCH, not an invalid date, and `new Date("0")` is the year 2000 — both
    //perfectly finite numbers that no NaN or negative check would stop. See the
    //20,681-day note in demurrageDays.
    if (value instanceof Date) {
        return Number.isNaN(value.getTime())
            ? null
            : new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    if (typeof value !== "string") return null;
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (!ymd) return null;
    //Built from the parts rather than parsed: `new Date("2026-05-02")` is UTC
    //midnight by specification, which is the whole trap described above.
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Whole calendar days between two dates — MySQL's DATEDIFF, in the browser.
 *
 * Null when either end does not name a day, which the caller reads as "no
 * elapsed time" rather than as a number.
 */
const datediff = (from, to) => {
    const a = startOfLocalDay(from);
    const b = startOfLocalDay(to);
    if (!a || !b) return null;
    //Round, not floor: twice a year one of these spans crosses a DST change and
    //runs 23 or 25 hours, which is still one day.
    return Math.round((b - a) / 86400000);
};

/**
 * How long this vehicle has been accruing demurrage.
 *
 * TWO CLOCKS, because node/queries/costExpr.js has two. A vehicle still in the
 * bond is running and measures to today (DEMURRAGE_DAYS.accruing); a DELIVERED
 * one stopped at the handover, and every deal sheet except Committed reads that
 * frozen figure — `IFNULL(DATEDIFF(Deliveries.DeliveryDate, Sales.WarehouseDate), 0)`.
 *
 * The client had only the first branch, so the deal modal went on billing a
 * delivered vehicle for the days it spent in the customer's hands. On the
 * harness fixture — warehoused 2026-05-02, handed over 2026-08-30 — the costing
 * statement read 142 days against the sheet's 120, and the gap widens by one day
 * every day: 311,520 UGX of demurrage that does not exist, folded into Sub Total
 * and Total Cost and subtracted from the gross profit the deal's Summary panel
 * shows. Being a preview is not a licence to disagree with the row being
 * previewed.
 *
 * A vehicle still in stock cannot reach the frozen branch — qryGetInventory
 * selects no DeliveryDate, and ToCommitted deletes the Inventory row — so one
 * function is right on both screens.
 */
export function demurrageDays(car) {
    // The emptiness of WarehouseDate has to be caught BEFORE the subtraction,
    // not after. The old code reached for getDaysDifference, which does
    // `new Date(date1)`, and `new Date(null)` is not an invalid date — it is the
    // EPOCH. A vehicle with no warehouse date was therefore billed 20,681 days of
    // demurrage (~97m UGX at a 4,000/day rate), silently, as a plain finite number
    // that no NaN or negative guard would catch. It only stayed hidden because the
    // importer left DailyDemurrage null too, and 20681 * "" is 0.
    //
    // startOfLocalDay is where that is now caught, for every input rather than
    // for the three the old guard happened to test.
    //
    // `new Date()`, not `Date()`: the string form would have to be re-parsed,
    // and startOfLocalDay deliberately no longer accepts free-form strings.
    const elapsed = datediff(car.WarehouseDate, car.DeliveryDate || new Date());
    // Still clamped: an unreadable date gives null, and a future warehouse date —
    // or a delivery recorded before the vehicle ever reached the bond — gives a
    // negative, which would credit the vehicle with demurrage it never accrued.
    return Math.max(0, num(elapsed));
}

/**
 * Is this customer late RIGHT NOW — as opposed to having been late once?
 *
 * Both halves, because either alone is wrong: a balance with time left on the
 * plan is not past due, and a passed due date with nothing owed is a deal that
 * has been settled. That sentence was already written down in
 * dashboard/concernFocus.js, whose `pastDue` intent has filtered on both columns
 * since it was built — the Credit sheet's own "Overdue Customers" tile, the
 * DaysOverdue cell renderer and the deal modal's Overdue panel each asked only
 * the first half, so the dashboard and the sheet it opens disagreed about which
 * customers were in trouble.
 *
 * It matters more since 2026-09-21 than it did before. `DaysOverdue` now FREEZES
 * at the payment that settled the deal instead of running for ever (owner's
 * call: the lateness a customer actually incurred is worth keeping), so a
 * settled deal can legitimately report 5 days overdue permanently. A positive
 * day count no longer implies anybody owes anything, and every screen that reads
 * it as "in trouble" has to say so itself. See queries/costExpr.js OVERDUE_DAYS.
 */
export function isPastDue(row) {
    return num(row?.DaysOverdue) > 0 && num(row?.TotalBalance) > 0;
}

export function computeDerivedCosts(car, vatRate, { commission = "target" } = {}) {
    const purchasePrice = num(car.PurchasePrice);
    const shippingCost = num(car.ShippingCost);
    const exchangeRate = num(car.ExchangeRate);

    const CifMombasa = car.Origin === "Local" ? 0 : purchasePrice + shippingCost;

    // A missing or unreadable WarehouseDate means the vehicle is not in a bond yet,
    // so no demurrage has accrued; a DeliveryDate means it has stopped accruing.
    // Both live in demurrageDays above, with the reasoning.
    const Days = demurrageDays(car);

    const TotalDemurrage = Days * num(car.DailyDemurrage) * num(vatRate);
    const commissionUGX = commission === "agreed"
        ? num(car.AgreedCommission) * (car.CommissionCurrency === "USD" ? exchangeRate : 1)
        : num(car.TargetCommission);
    const TotalCost = (purchasePrice + shippingCost + num(car.LandTranCost) + num(car.ClearingCost)) * exchangeRate
        + num(car.ClearingUG)
        + TotalDemurrage + num(car.OtherExpenses) + commissionUGX;
    const TotalCostWithDuty = TotalCost + num(car.Duty);
    return { CifMombasa, Days, TotalDemurrage, TotalCost, TotalCostWithDuty };
}

/**
 * The deal amounts that follow from the agreed terms.
 *
 * These used to be computed as a side effect of rendering EditSaleDetails,
 * which worked only because the old modal rendered every tab at once and hid
 * the inactive ones with display:none. The record shell renders one section at
 * a time, so a value derived inside a child that is not mounted is simply
 * absent — the delivery precondition read "release balance 0" and offered to
 * deliver a vehicle that had not been paid for.
 *
 * Mirrors the same expressions the server uses; a cash deal has no credit and
 * therefore no monthly payment.
 */
export function computeDealAmounts(deal) {
    const credit = deal.CashCredit === "Credit";
    const agreed = num(deal.AgreedPrice);
    const release = num(deal.ReleaseAmount);
    const period = num(deal.CreditPeriod);
    const paid = num(deal.TotalPaid);

    const CreditAmount = Math.round(credit ? agreed - release : 0);
    // Guard the divisor: a credit deal with no period yet would otherwise give
    // Infinity, which formats as a blank and reads as "not set".
    const MonthlyPayment = Math.round(credit && period > 0 ? (agreed - release) / period : 0);
    // Overpayment against the release is not a negative balance owing.
    const ReleaseBalance = Math.round(paid > release ? 0 : release - paid);

    // How much of the CREDIT has actually come in, which is not the same
    // question as how much of the price has. Payments settle the release first,
    // so a deal can be 91% "paid" while none of the credit has been serviced at
    // all — 100m car, 90m release, 10m credit, one 1m instalment in four months
    // reads 91% against the price and 10% against the credit. The second number
    // is the one that says whether this customer is paying.
    //
    // Clamped at zero: before the release is settled this subtraction is
    // negative, and negative collection is not a thing — that deal has simply
    // not reached the credit phase yet.
    const CreditCollected = Math.max(0, Math.round(paid - release));
    // NULL, not 0, when there is no credit. Cash sets ReleaseAmount to the
    // agreed price (AddEditCommitted's CashCredit handler), so CreditAmount is
    // 0 and this ratio has no denominator. A zero here would draw an empty
    // meter on a fully paid cash deal, which is the opposite of the truth;
    // null lets the caller pick a different meter instead.
    const CreditCollectedPct = CreditAmount > 0
        ? Math.min(100, Math.round((CreditCollected / CreditAmount) * 100))
        : null;

    return { CreditAmount, MonthlyPayment, ReleaseBalance, CreditCollected, CreditCollectedPct };
}
